/**
 * session-recorder/useSessionRecorder.ts
 * 
 * Hook global que grava as interações do usuário em formato de fluxo de eventos
 * ultra-econômico, com pausa inteligente após 3s de inatividade, geolocalização e versão.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../../store/authStore';
import { useAppVersionTelemetry } from '../useAppVersionTelemetry';
import type { RecordedSession, SessionEvent } from './types';
import { 
  isSessionRecorderEnabled, 
  getOrCreateTesterDeviceId, 
  getTesterDeviceInfo, 
  getTesterLocation, 
  saveRecordedSessionLocally 
} from './sessionStorageService';

const IDLE_THRESHOLD_MS = 3000; // 3 segundos sem atividade para pausar
const MOUSE_THROTTLE_MS = 150; // Amostragem de cursor a cada 150ms

export function useSessionRecorder() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const telemetry = useAppVersionTelemetry();
  const { localManifest, isStale } = telemetry;

  const sessionRef = useRef<RecordedSession | null>(null);
  const eventsRef = useRef<SessionEvent[]>([]);
  const startTimeRef = useRef<number>(Date.now());
  const lastActivityRef = useRef<number>(Date.now());
  const isIdleRef = useRef<boolean>(false);
  const lastMousePosRef = useRef<{ x: number; y: number; time: number }>({ x: 0, y: 0, time: 0 });

  // 1. Inicializa a sessão quando o app monta ou usuário loga
  useEffect(() => {
    if (!isSessionRecorderEnabled()) return;

    const deviceId = getOrCreateTesterDeviceId();
    const sessionId = `ses_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    startTimeRef.current = Date.now();
    lastActivityRef.current = Date.now();
    eventsRef.current = [];

    const newSession: RecordedSession = {
      id: sessionId,
      testerDeviceId: deviceId,
      user: {
        id: user?.id || 'anonimo',
        nome: user?.nome || 'Usuário Não Autenticado',
        email: user?.email || 'anonimo@alunoconectado.local',
        perfil: user?.perfil || 'VISITANTE',
        cargo: user?.cargo,
        escolaId: user?.escolaId,
        nomeEscola: user?.nomeEscola,
      },
      location: null,
      appVersion: {
        version: localManifest.version,
        gitCommit: localManifest.gitCommit,
        gitBranch: localManifest.gitBranch,
        buildTimestamp: localManifest.buildTimestamp,
        isStaleAgainstCdn: isStale,
      },
      device: getTesterDeviceInfo(),
      startedAt: new Date().toISOString(),
      durationSeconds: 0,
      activeSeconds: 0,
      idleSeconds: 0,
      initialRoute: location.pathname,
      finalRoute: location.pathname,
      totalEvents: 0,
      totalClicks: 0,
      totalErrors: 0,
      visitedRoutes: [location.pathname],
      events: [],
    };

    sessionRef.current = newSession;

    // Busca geolocalização assincronamente em background
    void getTesterLocation().then((loc) => {
      if (sessionRef.current && loc) {
        sessionRef.current.location = loc;
        saveRecordedSessionLocally(sessionRef.current);
      }
    });

    // Registra o evento inicial de rota
    addEvent({
      type: 'route_change',
      timestamp: 0,
      path: location.pathname,
      title: document.title,
    });

    // Salva periodicamente a cada 10s
    const interval = setInterval(() => {
      flushSession();
    }, 10000);

    // Salva ao fechar ou descarregar a página
    const handleUnload = () => {
      flushSession();
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
      flushSession();
    };
  }, [user?.id]);

  // Função auxiliar para registrar eventos com checagem de inatividade de 3s
  const addEvent = (event: SessionEvent) => {
    if (!isSessionRecorderEnabled()) return;

    const now = Date.now();
    const elapsedSinceLastActivity = now - lastActivityRef.current;

    // Se estava ocioso por mais de 3 segundos e uma nova ação aconteceu:
    if (elapsedSinceLastActivity > IDLE_THRESHOLD_MS && !isIdleRef.current) {
      isIdleRef.current = true;
      eventsRef.current.push({
        type: 'pause_idle',
        timestamp: lastActivityRef.current - startTimeRef.current,
        idleDurationMs: elapsedSinceLastActivity,
      });
    }

    if (isIdleRef.current && event.type !== 'pause_idle') {
      isIdleRef.current = false;
      eventsRef.current.push({
        type: 'resume_active',
        timestamp: now - startTimeRef.current,
      });
    }

    lastActivityRef.current = now;
    eventsRef.current.push(event);
  };

  // Consolida e grava a sessão no storage local
  const flushSession = () => {
    if (!sessionRef.current || eventsRef.current.length === 0) return;

    const now = Date.now();
    const totalDuration = Math.round((now - startTimeRef.current) / 1000);

    // Calcula tempo ocioso somando pausas registradas
    let idleMsTotal = 0;
    eventsRef.current.forEach((ev) => {
      if (ev.type === 'pause_idle' && ev.idleDurationMs) {
        idleMsTotal += ev.idleDurationMs;
      }
    });

    const idleSeconds = Math.round(idleMsTotal / 1000);
    const activeSeconds = Math.max(1, totalDuration - idleSeconds);

    const clicks = eventsRef.current.filter((e) => e.type === 'mouse_click').length;
    const errors = eventsRef.current.filter((e) => e.type === 'error').length;
    const routes = Array.from(
      new Set(
        eventsRef.current
          .filter((e) => e.type === 'route_change')
          .map((e) => (e as any).path)
      )
    );

    sessionRef.current.endedAt = new Date().toISOString();
    sessionRef.current.durationSeconds = totalDuration;
    sessionRef.current.activeSeconds = activeSeconds;
    sessionRef.current.idleSeconds = idleSeconds;
    sessionRef.current.finalRoute = location.pathname;
    sessionRef.current.totalEvents = eventsRef.current.length;
    sessionRef.current.totalClicks = clicks;
    sessionRef.current.totalErrors = errors;
    sessionRef.current.visitedRoutes = routes.length > 0 ? routes : [location.pathname];
    sessionRef.current.events = [...eventsRef.current];

    saveRecordedSessionLocally(sessionRef.current);
  };

  // 2. Monitora mudanças de rota do React Router
  useEffect(() => {
    if (!isSessionRecorderEnabled() || !sessionRef.current) return;

    const offset = Date.now() - startTimeRef.current;
    addEvent({
      type: 'route_change',
      timestamp: offset,
      path: location.pathname + location.search,
      title: document.title,
    });

    flushSession();
  }, [location.pathname, location.search]);

  // 3. Listeners globais de Interação (Clicks, Scroll, MouseMove, Erros)
  useEffect(() => {
    if (!isSessionRecorderEnabled()) return;

    // Listener de cliques
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const targetTag = target?.tagName?.toLowerCase() || 'div';
      let targetText = target?.innerText?.substring(0, 40) || target?.getAttribute('aria-label') || '';
      targetText = targetText.replace(/\s+/g, ' ').trim();

      const offset = Date.now() - startTimeRef.current;
      addEvent({
        type: 'mouse_click',
        timestamp: offset,
        x: Math.round(e.clientX),
        y: Math.round(e.clientY),
        targetTag,
        targetText: targetText || undefined,
        targetId: target?.id || undefined,
        targetClass: target?.className ? String(target.className).substring(0, 30) : undefined,
      });
    };

    // Listener de movimento de mouse (throttled para máxima leveza)
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastMousePosRef.current.time > MOUSE_THROTTLE_MS) {
        lastMousePosRef.current = { x: e.clientX, y: e.clientY, time: now };
        const offset = now - startTimeRef.current;
        addEvent({
          type: 'mouse_move',
          timestamp: offset,
          x: Math.round(e.clientX),
          y: Math.round(e.clientY),
        });
      }
    };

    // Listener de scroll
    let scrollTimeout: any = null;
    const handleScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        scrollTimeout = null;
        const offset = Date.now() - startTimeRef.current;
        addEvent({
          type: 'scroll',
          timestamp: offset,
          scrollY: Math.round(window.scrollY),
          scrollX: Math.round(window.scrollX),
          path: location.pathname,
        });
      }, 200);
    };

    // Listener de erros de Javascript em runtime
    const handleError = (ev: ErrorEvent | PromiseRejectionEvent) => {
      const msg = 'message' in ev ? ev.message : String((ev as any).reason);
      const offset = Date.now() - startTimeRef.current;
      addEvent({
        type: 'error',
        timestamp: offset,
        message: msg.substring(0, 150),
        path: location.pathname,
      });
    };

    // Listener de erros HTTP emitidos pela camada de API (Axios client)
    const handleClientHttpError = (ev: Event) => {
      const detail = (ev as CustomEvent)?.detail;
      if (!detail) return;
      const offset = Date.now() - startTimeRef.current;
      const msg = detail.message || `Erro HTTP ${detail.status || ''} em ${detail.url || ''}`;
      addEvent({
        type: 'error',
        timestamp: offset,
        message: String(msg).substring(0, 200),
        path: location.pathname,
      });
    };

    window.addEventListener('click', handleClick, { passive: true, capture: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('error', handleError as any);
    window.addEventListener('unhandledrejection', handleError as any);
    window.addEventListener('ac:client-error', handleClientHttpError as any);

    return () => {
      window.removeEventListener('click', handleClick, { capture: true });
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('error', handleError as any);
      window.removeEventListener('unhandledrejection', handleError as any);
      window.removeEventListener('ac:client-error', handleClientHttpError as any);
    };
  }, [location.pathname]);

  return {
    isRecording: isSessionRecorderEnabled(),
    flushSession,
  };
}
